import { useMemo } from "react";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import { Card, CardContent, Typography } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { RectColorTag } from "@/components/tags";
import { SyncHistory } from "@/types/integration";
import { TableHeaderType } from "@/components/tables/types";

interface DbtCoreSyncHistoryContentProps {
  syncHistory: SyncHistory[];
}

type SyncHistoryItem = SyncHistory & { id: string };

const DEFAULT_PAGE_SIZE = 10;

const DbtCoreSyncHistoryContent = ({
  syncHistory,
}: DbtCoreSyncHistoryContentProps): JSX.Element => {
  const { page, pageSize, setPage, setPageSize } = useTablePagination();
  const headers: TableHeaderType<SyncHistoryItem>[] = [
    {
      id: 1,
      label: "Type",
      uid: "type",
      width: "20%",
      render: item => (
        <Typography size="sm" weight="medium">
          {capitalize(item.type)}
        </Typography>
      ),
    },
    {
      id: 2,
      label: "Time",
      uid: "time",
      width: "30%",
      render: item => (
        <Typography size="sm">
          {item.time ? dayjs(item.time).format("h:mm A, D MMMM YYYY") : "-"}
        </Typography>
      ),
    },
    {
      id: 3,
      label: "Status",
      uid: "status",
      width: "20%",
      render: item => {
        const getStatusColor = (type: string) => {
          switch (type.toLowerCase()) {
            case "completed":
            case "success":
              return "lightGreen";
            case "failed":
            case "error":
              return "pink";
            default:
              return "purple";
          }
        };

        const getStatusLabel = (type: string) => {
          switch (type.toLowerCase()) {
            case "completed":
              return "Success";
            case "failed":
              return "Failed";
            default:
              return capitalize(type);
          }
        };

        return <RectColorTag label={getStatusLabel(item.type)} color={getStatusColor(item.type)} />;
      },
    },
    {
      id: 4,
      label: "Comments",
      uid: "comments",
      width: "30%",
      render: item => {
        const getComment = (type: string) => {
          switch (type.toLowerCase()) {
            case "completed":
            case "success":
              return "Sync completed successfully";
            case "failed":
            case "error":
              return "Sync failed";
            default:
              return "Sync in progress";
          }
        };

        return (
          <Typography size="sm" className="al-text-muted-foreground">
            {getComment(item.type)}
          </Typography>
        );
      },
    },
  ];

  // Map all items with IDs
  const allItems: SyncHistoryItem[] = useMemo(
    () =>
      syncHistory.map((item, index) => ({
        ...item,
        id: `${item.type}-${item.time}-${index}`,
      })),
    [syncHistory]
  );

  // Calculate pagination values
  const currentPageSize = pageSize || DEFAULT_PAGE_SIZE;
  const totalItems = allItems.length;
  const totalPages = Math.ceil(totalItems / currentPageSize);

  // Get paginated items for current page
  const paginatedItems = useMemo(() => {
    const startIndex = page * currentPageSize;
    const endIndex = startIndex + currentPageSize;
    return allItems.slice(startIndex, endIndex);
  }, [allItems, page, currentPageSize]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  return (
    <Card>
      <CardContent className="al-pt-4 al-pl-4">
        <Typography variant="section-heading" className="al-mb-2">
          Sync History
        </Typography>
        <DataTable<SyncHistoryItem>
          header={headers}
          data={{ items: paginatedItems }}
          idKey={item => item.id}
        />
        {allItems.length === 0 && (
          <Typography className="al-text-center al-text-muted-foreground al-py-8">
            No sync history available. Run the datapilot command to sync your dbt artifacts.
          </Typography>
        )}
        {totalItems > 0 && (
          <Paginate
            itemCount={totalItems}
            page={page}
            pageSize={currentPageSize}
            numPages={totalPages}
            onPageClick={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSize
          />
        )}
      </CardContent>
    </Card>
  );
};

export default DbtCoreSyncHistoryContent;
